/* © NXAPP Limited */
/**
 * Weekly Email (AI) — the tab inside a campaign of kind 'weekly'. Holds ONE
 * brief (the AI's rulebook) + signature for this campaign, and the queue of
 * upcoming weekly drafts. Body and signature use the full rich editor; audience,
 * sending profile and auto-send are the campaign's own settings.
 */
import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { useAuthContext } from '@/context/AuthContext'
import { authedFetch } from '@/lib/authedFetch'
import { describeError } from '@/lib/describeError'
import { CalendarClock, Sparkles, Lock, Unlock, Check, RefreshCw, X, AlertTriangle, BarChart3, Send } from 'lucide-react'
import EmptyState from '@/components/ui/EmptyState'
import SimpleEmailEditor from '@/components/builder/SimpleEmailEditor'

const DOW = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday']

interface Brief {
  id: string; name: string; campaign_id: string | null; brief: string; signature: string
  schedule_dow: number; schedule_hour: number; schedule_minute: number; buffer_weeks: number
  no_repeat_weeks: number; topic_bank: string[]; use_web_search: boolean; auto_draft: boolean
  require_approval: boolean; active: boolean; intro_enabled: boolean; intro_since: string | null
}
interface LData { subject?: string; html?: string; body?: string; topic?: string; reviewFlag?: boolean; locked?: boolean }
interface LetterIssue { id: string; subject: string | null; status: string; scheduled_at: string | null; design_json: { mode?: string; briefId?: string; role?: string; data?: LData; autoSentByAi?: boolean } | null }

const WRAP_OPEN = '<div style="font-family:-apple-system,BlinkMacSystemFont,\'Segoe UI\',Roboto,Helvetica,Arial,sans-serif;font-size:15px;line-height:1.6;color:#1f2937;max-width:640px;margin:0 auto;padding:24px;">'
function textToHtml(text: string): string {
  const esc = (s: string) => (s ?? '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
  const paras = esc(text || '').split(/\n{2,}/).map(p => `<p style="margin:0 0 16px;">${p.replace(/\n/g, '<br/>')}</p>`).join('')
  return WRAP_OPEN + (paras || '<p><br/></p>') + '</div>'
}
function finalize(bodyHtml: string, sigHtml: string): string {
  const body = (bodyHtml || '').replace(/<p>/gi, '<p style="margin:0 0 16px;">').replace(/<div>/gi, '<div style="margin:0 0 16px;">')
  const sig = sigHtml && sigHtml.trim() ? `<div style="margin-top:8px;">${sigHtml}</div>` : ''
  let out = body.replace(/\[SIGNATURE\]/gi, '') + sig   // signature always last → P.S. sits above it
  if (!/\{\{UNSUBSCRIBE_URL\}\}/i.test(out)) {
    out += `<div style="text-align:left;padding:14px 24px 24px;font-size:11px;line-height:1.5;color:#9ca3af;font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,Helvetica,Arial,sans-serif;max-width:640px;margin:0 auto;">You received this email because you are connected with us or subscribed to our mailing list. You can unsubscribe at any time by clicking <a href="{{UNSUBSCRIBE_URL}}" style="color:#9ca3af;text-decoration:underline;">Unsubscribe</a>.<br/><span style="color:#c4c9d0;">{{companyName}} · {{companyAddress}}</span></div>`
  }
  return out
}
const fillPreview = (html: string) => html
  .replace(/\{\{firstName\}\}/gi, 'Jane').replace(/\{\{lastName\}\}/gi, 'Smith').replace(/\{\{company\}\}/gi, 'Acme Ltd')
  .replace(/\{\{companyName\}\}/gi, 'Your Company').replace(/\{\{companyAddress\}\}/gi, 'London').replace(/\{\{UNSUBSCRIBE_URL\}\}/gi, '#')
const bodyOf = (d: LData) => d.html ?? (d.body ? textToHtml(d.body) : '')
const fmtLondon = (iso: string | null) => iso ? new Date(iso).toLocaleString('en-GB', { timeZone: 'Europe/London', weekday: 'short', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : '—'

export default function WeeklyContent({ campaignId }: { campaignId: string }) {
  const { user } = useAuthContext()
  const qc = useQueryClient()
  const [error, setError] = useState('')
  const [editing, setEditing] = useState<string | null>(null)
  const [subject, setSubject] = useState('')
  const [bodyHtml, setBodyHtml] = useState('')
  const [seed, setSeed] = useState('')            // what the editor is (re)seeded with
  const [editorNonce, setEditorNonce] = useState(0)
  const [topicHint, setTopicHint] = useState('')
  const [genBusy, setGenBusy] = useState(false)
  const [fillBusy, setFillBusy] = useState(false)
  const [fillMsg, setFillMsg] = useState('')
  const [sigHtml, setSigHtml] = useState('')
  const [testEmail, setTestEmail] = useState('')
  const [testBusy, setTestBusy] = useState(false)
  const [testMsg, setTestMsg] = useState('')
  const [introBusy, setIntroBusy] = useState(false)
  const [introMsg, setIntroMsg] = useState('')
  const [sigMode, setSigMode] = useState<'rich' | 'html'>('rich')
  const [sigSeed, setSigSeed] = useState('')
  const [sigNonce, setSigNonce] = useState(0)

  const { data: brief, isLoading } = useQuery({
    queryKey: ['weekly-brief', campaignId], enabled: !!user,
    queryFn: async () => {
      const { data } = await supabase.from('ai_letter_briefs').select('*').eq('campaign_id', campaignId).eq('user_id', user!.id).maybeSingle()
      return (data ?? null) as Brief | null
    },
  })
  useEffect(() => { if (brief) { setSigHtml(brief.signature || ''); setSigSeed(brief.signature || ''); setSigNonce(n => n + 1) } }, [brief?.id])   // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { if (user?.email && !testEmail) setTestEmail(user.email) }, [user?.email])   // eslint-disable-line react-hooks/exhaustive-deps

  const { data: letters } = useQuery({
    queryKey: ['weekly-letters', campaignId, brief?.id], enabled: !!brief,
    queryFn: async () => {
      const { data } = await supabase.from('issues').select('id, subject, status, scheduled_at, design_json').eq('campaign_id', campaignId).eq('user_id', user!.id).order('scheduled_at', { ascending: true }).limit(200)
      return ((data ?? []) as LetterIssue[]).filter(i => i.design_json?.mode === 'letter' && i.design_json?.briefId === brief!.id)
    },
  })

  // Approx audience size (the "to send" total) from the campaign's default audience.
  const { data: audienceTotal } = useQuery({
    queryKey: ['weekly-audience', campaignId], enabled: !!brief,
    queryFn: async () => {
      const { data: camp } = await supabase.from('campaigns').select('default_audience').eq('id', campaignId).single()
      const da = (camp as { default_audience: { allSubscribed?: boolean; segmentIds?: string[]; campaignLeads?: boolean } | null } | null)?.default_audience
      if (da?.segmentIds?.length && !da.allSubscribed) {
        const { data: sc } = await supabase.from('segment_contacts').select('contact_id').in('segment_id', da.segmentIds)
        return new Set((sc ?? []).map(r => r.contact_id as string)).size
      }
      if (da?.allSubscribed) {
        const { count } = await supabase.from('contacts').select('id', { count: 'exact', head: true }).eq('user_id', user!.id).eq('status', 'SUBSCRIBED')
        return count ?? 0
      }
      // Default (and campaignLeads): the campaign's members.
      const { count } = await supabase.from('lead_campaign_assignments').select('id', { count: 'exact', head: true }).eq('campaign_id', campaignId).eq('status', 'active')
      return count ?? 0
    },
  })

  // Per-issue engagement (sent / opens / clicks / unsubs) for the rows.
  const sendingNow = (letters ?? []).some(l => l.status === 'SENDING')
  const { data: letterStats } = useQuery({
    queryKey: ['weekly-stats', campaignId, (letters ?? []).map(l => l.id).join(',')],
    enabled: !!(letters ?? []).some(l => l.status !== 'DRAFT'),
    refetchInterval: sendingNow ? 10_000 : false,
    queryFn: async () => {
      const ids = (letters ?? []).filter(l => l.status !== 'DRAFT').map(l => l.id)
      if (!ids.length) return new Map<string, { total: number; sent: number; open: number; click: number; unsub: number }>()
      const { data } = await supabase.from('sends').select('issue_id, status, events(type)').in('issue_id', ids).limit(20000)
      const map = new Map<string, { total: number; sent: number; open: number; click: number; unsub: number }>()
      for (const s of (data ?? []) as { issue_id: string; status: string; events: { type: string }[] }[]) {
        const m = map.get(s.issue_id) ?? { total: 0, sent: 0, open: 0, click: 0, unsub: 0 }
        m.total++
        if (s.status === 'SENT' || s.status === 'DELIVERED') m.sent++
        const ev = s.events ?? []
        if (ev.some(e => e.type === 'OPEN')) m.open++
        if (ev.some(e => e.type === 'CLICK')) m.click++
        if (ev.some(e => e.type === 'UNSUBSCRIBE')) m.unsub++
        map.set(s.issue_id, m)
      }
      return map
    },
  })

  // Read-only "why did/didn't the intro send" — shown automatically on the card.
  const { data: introStatus } = useQuery({
    queryKey: ['intro-status', brief?.id, brief?.intro_enabled, brief?.intro_since, letters?.length],
    enabled: !!brief?.intro_enabled,
    queryFn: async () => {
      const res = await authedFetch('/api/letter/intro-status', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ briefId: brief!.id }) })
      return await res.json() as { ok?: boolean; hasContent?: boolean; hasProfile?: boolean; eligible?: number; alreadySent?: number; reason?: string }
    },
  })

  const createBrief = useMutation({
    mutationFn: async () => {
      const { data: camp } = await supabase.from('campaigns').select('name, sending_profile_id').eq('id', campaignId).single()
      const { error } = await supabase.from('ai_letter_briefs').insert({
        user_id: user!.id, campaign_id: campaignId, name: (camp as { name: string } | null)?.name ?? 'Weekly email',
        sending_profile_id: (camp as { sending_profile_id: string | null } | null)?.sending_profile_id ?? null,
      })
      if (error) throw error
    },
    onError: e => setError(describeError(e)),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['weekly-brief', campaignId] }),
  })

  const updateBrief = useMutation({
    mutationFn: async (patch: Partial<Brief>) => { const { error } = await supabase.from('ai_letter_briefs').update(patch).eq('id', brief!.id); if (error) throw error },
    onError: e => setError(describeError(e)),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['weekly-brief', campaignId] }),
  })

  const saveLetter = useMutation({
    mutationFn: async ({ issue, data }: { issue: LetterIssue; data: LData }) => {
      const html = finalize(data.html ?? '', sigHtml)
      const role = issue.design_json?.role
      const dj: Record<string, unknown> = { mode: 'letter', briefId: brief!.id, data }
      if (role) dj.role = role
      const { error } = await supabase.from('issues').update({ subject: data.subject || brief!.name, html, design_json: dj }).eq('id', issue.id)
      if (error) throw error
    },
    onError: e => setError(describeError(e)),
    onSuccess: () => { setEditing(null); qc.invalidateQueries({ queryKey: ['weekly-letters', campaignId] }) },
  })

  const createIntro = useMutation({
    mutationFn: async () => {
      const { data: camp } = await supabase.from('campaigns').select('sending_profile_id').eq('id', campaignId).single()
      const { error } = await supabase.from('issues').insert({
        user_id: user!.id, campaign_id: campaignId, sending_profile_id: (camp as { sending_profile_id: string | null } | null)?.sending_profile_id ?? null,
        subject: '(intro — draft)', html: '', status: 'DRAFT', scheduled_at: null, audience: null,
        design_json: { mode: 'letter', briefId: brief!.id, role: 'intro', data: { subject: '', html: '', locked: false } },
      })
      if (error) throw error
    },
    onError: e => setError(describeError(e)),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['weekly-letters', campaignId] }),
  })

  const setStatus = useMutation({
    mutationFn: async ({ id, status }: { id: string; status: string }) => { const { error } = await supabase.from('issues').update({ status }).eq('id', id); if (error) throw error },
    onError: e => setError(describeError(e)),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['weekly-letters', campaignId] }),
  })

  const deleteLetter = useMutation({
    mutationFn: async (id: string) => { const { error } = await supabase.from('issues').delete().eq('id', id); if (error) throw error },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['weekly-letters', campaignId] }),
  })

  async function generate(mode: 'full' | 'rewrite') {
    if (!brief) return
    setGenBusy(true); setError('')
    try {
      const res = await authedFetch('/api/letter/generate', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ briefId: brief.id, mode, topicHint: topicHint || undefined, currentBody: mode === 'rewrite' ? bodyHtml : undefined }) })
      const text = await res.text()
      let r: { ok?: boolean; subject?: string; bodyHtml?: string; error?: string }
      try { r = JSON.parse(text) } catch { throw new Error(res.ok ? 'Unexpected response' : `Server error (${res.status})`) }
      if (!res.ok || r.error) throw new Error(r.error || 'Draft failed')
      setSubject(r.subject ?? subject)
      setBodyHtml(r.bodyHtml ?? '')
      setSeed(r.bodyHtml ?? ''); setEditorNonce(n => n + 1)   // reseed the editor
    } catch (e) { setError(describeError(e)) } finally { setGenBusy(false) }
  }

  async function fillDrafts() {
    if (!brief) return
    setFillBusy(true); setError('')
    try {
      const target = brief.buffer_weeks
      for (let n = 0; n < target + 1; n++) {
        const res = await authedFetch('/api/letter/fill', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ briefId: brief.id }) })
        const r = await res.json() as { ok?: boolean; created?: number; future?: number; error?: string }
        if (!res.ok || r.ok === false) throw new Error(r.error || 'Could not build drafts')
        qc.invalidateQueries({ queryKey: ['weekly-letters', campaignId] })
        setFillMsg(`Building drafts… ${r.future ?? 0}/${target}`)
        if (!r.created || (r.future ?? 0) >= target) break
      }
    } catch (e) { setError(describeError(e)) } finally { setFillBusy(false); setFillMsg('') }
  }

  // Save the current edits, then send a [TEST] copy of this draft to yourself.
  async function sendTest(l: LetterIssue) {
    if (!brief) return
    if (!testEmail) { setError('Enter an email to send the test to.'); return }
    setTestBusy(true); setTestMsg(''); setError('')
    try {
      const data: LData = { ...(l.design_json?.data ?? {}), subject, html: bodyHtml }
      const html = finalize(bodyHtml, sigHtml)
      const dj: Record<string, unknown> = { mode: 'letter', briefId: brief.id, data }
      if (l.design_json?.role) dj.role = l.design_json.role          // keep 'intro' — don't turn it into a weekly draft
      const { error: upErr } = await supabase.from('issues').update({ subject: subject || brief.name, html, design_json: dj }).eq('id', l.id)
      if (upErr) throw upErr
      const res = await authedFetch('/api/send-test', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ issueId: l.id, toEmail: testEmail }) })
      if (!res.ok) throw new Error((await res.text()) || `Failed (${res.status})`)
      setTestMsg(`Test sent to ${testEmail}`)
      qc.invalidateQueries({ queryKey: ['weekly-letters', campaignId] })
    } catch (e) { setError(describeError(e)) } finally { setTestBusy(false) }
  }

  async function runIntroNow() {
    if (!brief) return
    setIntroBusy(true); setIntroMsg(''); setError('')
    try {
      const res = await authedFetch('/api/letter/intro-run', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ briefId: brief.id }) })
      const r = await res.json() as { ok?: boolean; sent?: number; reason?: string }
      if (!res.ok) throw new Error(r.reason || `Failed (${res.status})`)
      setIntroMsg(r.sent ? `Sent the intro to ${r.sent} new subscriber${r.sent === 1 ? '' : 's'}.` : (r.reason || 'Nothing to send.'))
      qc.invalidateQueries({ queryKey: ['weekly-letters', campaignId] })
    } catch (e) { setError(describeError(e)) } finally { setIntroBusy(false) }
  }

  const startEdit = (l: LetterIssue) => {
    const d = l.design_json?.data ?? {}
    setEditing(l.id); setTopicHint('')
    setSubject(d.subject ?? l.subject ?? '')
    const b = bodyOf(d); setBodyHtml(b); setSeed(b); setEditorNonce(n => n + 1)
  }

  // The shared inline editor, used by both the weekly drafts and the intro email.
  const renderEditor = (l: LetterIssue, isIntro: boolean) => {
    const d = l.design_json?.data ?? {}
    return (
      <div className="mt-3 space-y-2 border-t border-gray-100 pt-3">
        <div className="flex items-center gap-2 flex-wrap">
          <input value={topicHint} onChange={e => setTopicHint(e.target.value)} placeholder={isIntro ? 'What the intro should say (optional)' : 'Topic to write about (optional)'} className="input py-1 text-sm flex-1 min-w-[160px]" />
          <button onClick={() => generate('full')} disabled={genBusy} className="btn-secondary text-xs flex items-center gap-1 disabled:opacity-50"><Sparkles className="w-3.5 h-3.5" />{genBusy ? 'Writing…' : 'Generate'}</button>
          <button onClick={() => generate('rewrite')} disabled={genBusy || !bodyHtml} className="btn-secondary text-xs flex items-center gap-1 disabled:opacity-50"><RefreshCw className="w-3.5 h-3.5" />Rewrite</button>
        </div>
        <input value={subject} onChange={e => setSubject(e.target.value)} placeholder="Subject" className="input py-1 text-sm" />
        <SimpleEmailEditor key={`ed-${l.id}-${editorNonce}`} initialHtml={seed} onChange={setBodyHtml} hidePreview />
        {bodyHtml && (
          <div className="rounded border border-gray-200 bg-gray-50 p-2">
            <p className="text-[10px] text-gray-400 mb-1">Preview (with signature, sample name)</p>
            <div className="bg-white rounded" dangerouslySetInnerHTML={{ __html: fillPreview(finalize(bodyHtml, sigHtml)) }} />
          </div>
        )}
        <div className="flex items-center gap-2 flex-wrap">
          <button onClick={() => saveLetter.mutate({ issue: l, data: { ...d, subject, html: bodyHtml } })} disabled={saveLetter.isPending} className="btn-primary text-xs disabled:opacity-50">Save draft</button>
          <button onClick={() => saveLetter.mutate({ issue: l, data: { ...d, subject, html: bodyHtml, locked: !d.locked } })} className="btn-secondary text-xs flex items-center gap-1">{d.locked ? <><Unlock className="w-3.5 h-3.5" />Unlock</> : <><Lock className="w-3.5 h-3.5" />Save &amp; lock</>}</button>
          {!isIntro && (l.status !== 'SCHEDULED'
            ? <button onClick={() => setStatus.mutate({ id: l.id, status: 'SCHEDULED' })} className="text-xs text-green-700 border border-green-300 rounded px-2 py-1 hover:bg-green-50 flex items-center gap-1"><Check className="w-3.5 h-3.5" />Approve &amp; schedule</button>
            : <button onClick={() => setStatus.mutate({ id: l.id, status: 'DRAFT' })} className="text-xs text-gray-500 border border-gray-300 rounded px-2 py-1 hover:bg-gray-50">Back to draft</button>)}
          <button onClick={() => setEditing(null)} className="text-xs text-gray-400 ml-auto">Close</button>
        </div>
        <div className="flex items-center gap-2 flex-wrap pt-1">
          <input value={testEmail} onChange={e => setTestEmail(e.target.value)} placeholder="you@example.com" className="input py-1 text-xs w-56" />
          <button onClick={() => sendTest(l)} disabled={testBusy || !bodyHtml} className="btn-secondary text-xs flex items-center gap-1 disabled:opacity-50"><Send className="w-3.5 h-3.5" />{testBusy ? 'Sending…' : 'Send test to me'}</button>
          {testMsg && <span className="text-[11px] text-green-600">{testMsg}</span>}
        </div>
        <p className="text-[10px] text-gray-400">{isIntro
          ? 'Saved automatically becomes the intro. It sends once to each new subscriber (with open & click tracking). Lock to stop the AI ever changing it.'
          : 'Approve & schedule sends it at its date/time above. If you do nothing and the campaign’s Auto-send is on, it still sends then (AI fills it if empty) — locked drafts are never overwritten.'}</p>
      </div>
    )
  }

  const introIssue = (letters ?? []).find(l => l.design_json?.role === 'intro') ?? null
  const weeklyDrafts = (letters ?? []).filter(l => l.design_json?.role !== 'intro')

  if (isLoading) return <p className="text-sm text-gray-400 py-6">Loading…</p>
  if (!brief) return (
    <div className="card p-6 text-center space-y-3">
      <EmptyState icon={CalendarClock} title="Set up this weekly email" description="Create the AI brief that this campaign's weekly email is written from." />
      <button onClick={() => createBrief.mutate()} disabled={createBrief.isPending} className="btn-primary text-sm">Set up weekly email</button>
      {error && <p className="text-sm text-red-600">{error}</p>}
    </div>
  )

  return (
    <div className="space-y-4">
      {error && <div className="rounded-md bg-red-50 border border-red-200 px-4 py-2 text-sm text-red-700">{error}<button onClick={() => setError('')} className="ml-3 text-red-400 hover:text-red-600">✕</button></div>}

      {/* Schedule + draft toggles */}
      <div className="card p-4 space-y-3">
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs text-gray-600">
          <label>Send day
            <select value={brief.schedule_dow} onChange={e => updateBrief.mutate({ schedule_dow: Number(e.target.value) })} className="input py-1 mt-0.5 text-sm">
              {DOW.map((d, i) => <option key={i} value={i}>{d}</option>)}
            </select>
          </label>
          <label>Time (London)
            <input type="time" defaultValue={`${String(brief.schedule_hour).padStart(2, '0')}:${String(brief.schedule_minute).padStart(2, '0')}`} key={`t-${brief.id}`} onBlur={e => { const [h, m] = e.target.value.split(':').map(Number); if (h !== brief.schedule_hour || m !== brief.schedule_minute) updateBrief.mutate({ schedule_hour: h || 0, schedule_minute: m || 0 }) }} className="input py-1 mt-0.5 text-sm" />
          </label>
          <label>Drafts to keep ready
            <input type="number" min={1} max={12} defaultValue={brief.buffer_weeks} key={`bw-${brief.id}`} onBlur={e => Number(e.target.value) !== brief.buffer_weeks && updateBrief.mutate({ buffer_weeks: Number(e.target.value) })} className="input py-1 mt-0.5 text-sm" />
          </label>
          <label>Don't repeat topic (weeks)
            <input type="number" min={0} defaultValue={brief.no_repeat_weeks} key={`nr-${brief.id}`} onBlur={e => Number(e.target.value) !== brief.no_repeat_weeks && updateBrief.mutate({ no_repeat_weeks: Number(e.target.value) })} className="input py-1 mt-0.5 text-sm" />
          </label>
        </div>
        <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-gray-600">
          <label className="flex items-center gap-1"><input type="checkbox" checked={brief.auto_draft} onChange={e => updateBrief.mutate({ auto_draft: e.target.checked })} /> AI drafts each week</label>
          <label className="flex items-center gap-1"><input type="checkbox" checked={brief.use_web_search} onChange={e => updateBrief.mutate({ use_web_search: e.target.checked })} /> Check recent news</label>
          <label className="flex items-center gap-1"><input type="checkbox" checked={brief.active} onChange={e => updateBrief.mutate({ active: e.target.checked })} /> Active (keep drafting)</label>
        </div>
        <p className="text-[11px] text-gray-400">Who it goes to, which mailbox it sends from, and <b>auto-send if not approved</b> are this campaign's own settings (above). It sends to the campaign's audience/members like a broadcast, with open &amp; click tracking.</p>
      </div>

      {/* Brief + signature + topics */}
      <div className="card p-4 space-y-3">
        <div>
          <label className="text-xs font-medium text-gray-700">The brief (rules &amp; guardrails the AI must follow)</label>
          <textarea defaultValue={brief.brief} key={`br-${brief.id}`} rows={10} placeholder="Paste your full spec here — voice, prime rule, every guardrail, structure, do's and don'ts. This is the AI's rulebook for this email." onBlur={e => e.target.value !== brief.brief && updateBrief.mutate({ brief: e.target.value })} className="input text-xs font-mono mt-1 leading-relaxed" />
        </div>
        <div>
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <label className="text-xs font-medium text-gray-700">Signature (replaces [SIGNATURE]) — add an image, change fonts</label>
            <div className="flex items-center gap-2">
              <div className="inline-flex rounded-md border border-gray-200 overflow-hidden text-[11px]">
                <button onClick={() => { setSigSeed(sigHtml); setSigNonce(n => n + 1); setSigMode('rich') }} className={`px-2 py-1 ${sigMode === 'rich' ? 'bg-gray-900 text-white' : 'bg-white text-gray-600'}`}>Rich</button>
                <button onClick={() => setSigMode('html')} className={`px-2 py-1 ${sigMode === 'html' ? 'bg-gray-900 text-white' : 'bg-white text-gray-600'}`}>HTML code</button>
              </div>
              <button onClick={() => updateBrief.mutate({ signature: sigHtml })} disabled={updateBrief.isPending || sigHtml === brief.signature} className="btn-secondary text-[11px] py-1 disabled:opacity-40">Save signature</button>
            </div>
          </div>
          <div className="mt-1">
            {sigMode === 'rich' ? (
              <SimpleEmailEditor key={`sig-${brief.id}-${sigNonce}`} initialHtml={sigSeed} onChange={setSigHtml} hidePreview />
            ) : (
              <div className="space-y-2">
                <textarea value={sigHtml} onChange={e => setSigHtml(e.target.value)} rows={8} placeholder="Paste your signature HTML here (e.g. from your email client). It's used exactly as written." className="input text-xs font-mono leading-relaxed" />
                {sigHtml && (
                  <div className="rounded border border-gray-200 bg-gray-50 p-2">
                    <p className="text-[10px] text-gray-400 mb-1">Signature preview</p>
                    <div className="bg-white rounded p-2" dangerouslySetInnerHTML={{ __html: sigHtml }} />
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
        <div>
          <label className="text-xs font-medium text-gray-700">Topic bank (one per line, optional — if empty the AI picks a topic from the brief)</label>
          <textarea defaultValue={(brief.topic_bank ?? []).join('\n')} key={`tb-${brief.id}`} rows={3} placeholder={'Backups you have never tested\nThe founder inbox is the master key'} onBlur={e => { const arr = e.target.value.split('\n').map(s => s.trim()).filter(Boolean); if (JSON.stringify(arr) !== JSON.stringify(brief.topic_bank ?? [])) updateBrief.mutate({ topic_bank: arr }) }} className="input text-xs mt-1" />
        </div>
        <p className="text-[11px] text-gray-400">Use <code className="font-mono">{'{{firstName}}'}</code> for the recipient and put <code className="font-mono">[SIGNATURE]</code> where the sign-off goes. The unsubscribe footer is added automatically.</p>
      </div>

      {/* Intro email — auto-sent once to each new subscriber */}
      <div className="card p-4 space-y-2">
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <div className="min-w-0">
            <h3 className="text-sm font-semibold text-gray-800">Intro email</h3>
            <p className="text-[11px] text-gray-400">Sent once, automatically, to each new subscriber who joins after you switch it on (with open &amp; click tracking). New joiners already on the list before this stay untouched.</p>
          </div>
          <label className="flex items-center gap-1 text-xs text-gray-600 shrink-0"><input type="checkbox" checked={brief.intro_enabled} onChange={e => updateBrief.mutate({ intro_enabled: e.target.checked, intro_since: e.target.checked ? (brief.intro_since ?? new Date().toISOString()) : brief.intro_since })} /> Send to new joiners</label>
        </div>
        {brief.intro_enabled && (!introIssue ? (
          <button onClick={() => createIntro.mutate()} disabled={createIntro.isPending} className="btn-secondary text-xs">Set up the intro email</button>
        ) : (
          <div className="rounded-lg border border-gray-200 p-2">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-sm text-gray-900 flex-1 min-w-[160px] truncate">{introIssue.design_json?.data?.subject || introIssue.subject || <span className="text-gray-400">(empty — write or generate it)</span>}</span>
              {introIssue.design_json?.data?.locked && <span className="text-[10px] px-1.5 py-0.5 rounded-full border bg-gray-100 text-gray-600 border-gray-200 flex items-center gap-0.5"><Lock className="w-2.5 h-2.5" />locked</span>}
              <Link to={`/campaigns/${campaignId}/issues/${introIssue.id}/stats`} className="text-xs text-brand-600 hover:underline flex items-center gap-0.5"><BarChart3 className="w-3.5 h-3.5" />Stats</Link>
              {editing !== introIssue.id && <button onClick={() => startEdit(introIssue)} className="text-xs text-brand-600 hover:underline">Edit</button>}
              <button onClick={runIntroNow} disabled={introBusy} className="text-xs text-teal-700 border border-teal-300 rounded px-2 py-0.5 hover:bg-teal-50 disabled:opacity-50 flex items-center gap-1"><Send className="w-3.5 h-3.5" />{introBusy ? 'Sending…' : 'Send now to new joiners'}</button>
            </div>
            {introMsg && <p className="text-[11px] text-gray-600 mt-1">{introMsg}</p>}
            {introStatus?.reason && !introMsg && (
              <p className={`text-[11px] mt-1 ${(introStatus.eligible ?? 0) > 0 ? 'text-gray-600' : (introStatus.hasContent && introStatus.hasProfile ? 'text-gray-500' : 'text-amber-600')}`}>
                {introStatus.reason}{typeof introStatus.alreadySent === 'number' && introStatus.alreadySent > 0 ? ` · ${introStatus.alreadySent} sent so far.` : ''}
              </p>
            )}
            {editing === introIssue.id && renderEditor(introIssue, true)}
          </div>
        ))}
      </div>

      {/* Upcoming drafts */}
      <div className="card overflow-hidden">
        <div className="px-4 py-2 border-b border-gray-200 flex items-center justify-between gap-2 flex-wrap">
          <h3 className="text-sm font-semibold text-gray-800">Upcoming weekly drafts</h3>
          <div className="flex items-center gap-2">
            {fillMsg && <span className="text-[11px] text-gray-500">{fillMsg}</span>}
            <button onClick={fillDrafts} disabled={fillBusy || !brief.brief.trim()} title={!brief.brief.trim() ? 'Write the brief first' : `Build ${brief.buffer_weeks} drafts now`} className="btn-secondary text-xs flex items-center gap-1 disabled:opacity-50">
              <Sparkles className="w-3.5 h-3.5" />{fillBusy ? 'Building…' : `Generate ${brief.buffer_weeks} drafts`}
            </button>
          </div>
        </div>
        {!weeklyDrafts.length ? (
          <div className="p-6 text-center">
            <EmptyState icon={CalendarClock} title="No drafts yet" description={brief.brief.trim() ? `Click “Generate ${brief.buffer_weeks} drafts” to build them now, or they fill automatically on the next scheduler run (~15 min).` : 'Write the brief above first, then generate the drafts.'} />
          </div>
        ) : (
          <div className="divide-y divide-gray-100">
            {weeklyDrafts.map(l => {
              const d = l.design_json?.data ?? {}
              const isEdit = editing === l.id
              return (
                <div key={l.id} className="p-3">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-[11px] text-gray-500 w-40">{fmtLondon(l.scheduled_at)}</span>
                    <span className="text-sm text-gray-900 flex-1 min-w-[160px] truncate">{d.subject || l.subject || <span className="text-gray-400">(empty draft)</span>}</span>
                    {d.locked && <span className="text-[10px] px-1.5 py-0.5 rounded-full border bg-gray-100 text-gray-600 border-gray-200 flex items-center gap-0.5"><Lock className="w-2.5 h-2.5" />locked</span>}
                    {d.reviewFlag && <span className="text-[10px] px-1.5 py-0.5 rounded-full border bg-amber-50 text-amber-700 border-amber-200 flex items-center gap-0.5" title="Names a story/organisation or contains a number — check before send"><AlertTriangle className="w-2.5 h-2.5" />review</span>}
                    <span className={`text-[10px] px-1.5 py-0.5 rounded-full border ${l.status === 'SENT' ? 'bg-green-50 text-green-700 border-green-200' : l.status === 'SCHEDULED' ? 'bg-indigo-50 text-indigo-700 border-indigo-200' : 'bg-amber-50 text-amber-700 border-amber-200'}`}>{l.design_json?.autoSentByAi ? 'sent by AI' : l.status.toLowerCase()}</span>
                    {(l.status === 'SENT' || l.status === 'SENDING') && <Link to={`/campaigns/${campaignId}/issues/${l.id}/stats`} className="text-xs text-brand-600 hover:underline flex items-center gap-0.5"><BarChart3 className="w-3.5 h-3.5" />Stats</Link>}
                    {l.status !== 'SENT' && l.status !== 'SENDING' && !isEdit && <button onClick={() => startEdit(l)} className="text-xs text-brand-600 hover:underline">Edit</button>}
                    {l.status !== 'SENT' && l.status !== 'SENDING' && <button onClick={() => confirm('Delete this draft?') && deleteLetter.mutate(l.id)} className="p-1 text-gray-300 hover:text-red-600"><X className="w-3.5 h-3.5" /></button>}
                  </div>
                  {l.status !== 'DRAFT' && (() => {
                    const st = letterStats?.get(l.id) ?? { total: 0, sent: 0, open: 0, click: 0, unsub: 0 }
                    const denom = st.sent || 1
                    const target = audienceTotal ?? 0
                    const prog = target ? Math.min(100, Math.round((st.sent / target) * 100)) : 0
                    return (
                      <div className="mt-1.5 flex items-center gap-x-4 gap-y-1 flex-wrap text-[11px] text-gray-500">
                        <span>To send: <b className="text-gray-700">~{target || '—'}</b></span>
                        <span>Sent: <b className="text-gray-700">{st.sent.toLocaleString()}</b></span>
                        <span>Opens: <b className="text-green-700">{st.open}</b>{st.sent ? ` (${Math.round((st.open / denom) * 100)}%)` : ''}</span>
                        <span>Clicks: <b className="text-blue-700">{st.click}</b>{st.sent ? ` (${Math.round((st.click / denom) * 100)}%)` : ''}</span>
                        <span>Unsub: <b className="text-amber-700">{st.unsub}</b>{st.sent ? ` (${Math.round((st.unsub / denom) * 100)}%)` : ''}</span>
                        {l.status === 'SENDING' && (
                          <span className="flex items-center gap-1"><span className="w-24 h-1.5 rounded-full bg-gray-100 overflow-hidden inline-block"><span className="block h-full rounded-full bg-brand-500" style={{ width: `${prog}%` }} /></span> sending… {prog}%</span>
                        )}
                      </div>
                    )
                  })()}
                  {isEdit && renderEditor(l, false)}
                </div>
              )
            })}
          </div>
        )}
      </div>
    </div>
  )
}
